const MAX_IMAGES = 3;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;
/** Matches feedback description validation (StoreFeedbackRequest). */
const MAX_DESCRIPTION_CHARS = 50_000;
/** Per-image budget so several images + text stay under MAX_DESCRIPTION_CHARS. */
const MAX_EMBEDDED_DATA_URL_CHARS = 14_000;
const MAX_IMAGE_DIMENSION = 1600;
const SKIP_COMPRESS_BELOW_BYTES = 48_000;

export function validateFeedbackImageFiles(files: FileList | File[]): string | null {
    const list = Array.from(files);

    if (list.length > MAX_IMAGES) {
        return `You can attach up to ${MAX_IMAGES} images.`;
    }

    for (const file of list) {
        if (!file.type.startsWith('image/')) {
            return 'Only image files are allowed.';
        }

        if (file.size > MAX_IMAGE_BYTES) {
            return 'Each image must be 5 MB or smaller.';
        }
    }

    return null;
}

function escapeHtml(text: string): string {
    return text
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
}

function readFileAsDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => {
            if (typeof reader.result === 'string') {
                resolve(reader.result);

                return;
            }

            reject(new Error('Could not read image file.'));
        };

        reader.onerror = () => {
            reject(new Error('Could not read image file.'));
        };

        reader.readAsDataURL(file);
    });
}

async function compressRasterImage(file: File): Promise<string> {
    const bitmap = await createImageBitmap(file);
    const sourceWidth = bitmap.width;
    const sourceHeight = bitmap.height;
    const longestEdge = Math.max(sourceWidth, sourceHeight);
    let scale = Math.min(1, MAX_IMAGE_DIMENSION / longestEdge);

    const render = (renderScale: number, quality: number): string => {
        const width = Math.max(1, Math.round(sourceWidth * renderScale));
        const height = Math.max(1, Math.round(sourceHeight * renderScale));
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;

        const context = canvas.getContext('2d');

        if (!context) {
            throw new Error('Could not prepare image for embedding.');
        }

        context.drawImage(bitmap, 0, 0, width, height);

        return canvas.toDataURL('image/webp', quality);
    };

    try {
        let quality = 0.88;
        let dataUrl = render(scale, quality);

        while (dataUrl.length > MAX_EMBEDDED_DATA_URL_CHARS && quality > 0.45) {
            quality -= 0.08;
            dataUrl = render(scale, quality);
        }

        if (dataUrl.length > MAX_EMBEDDED_DATA_URL_CHARS) {
            scale *= 0.75;
            dataUrl = render(scale, 0.72);
        }

        if (dataUrl.length > MAX_EMBEDDED_DATA_URL_CHARS) {
            throw new Error(
                'That image is still too large. Try a smaller screenshot or fewer images.',
            );
        }

        return dataUrl;
    } finally {
        bitmap.close();
    }
}

async function dataUrlForWidgetImage(file: File): Promise<string> {
    if (
        file.size <= SKIP_COMPRESS_BELOW_BYTES ||
        file.type === 'image/gif' ||
        file.type === 'image/svg+xml'
    ) {
        const dataUrl = await readFileAsDataUrl(file);

        if (dataUrl.length > MAX_EMBEDDED_DATA_URL_CHARS) {
            throw new Error(
                'That image is too large to embed. Try a smaller file or screenshot.',
            );
        }

        return dataUrl;
    }

    return compressRasterImage(file);
}

/**
 * Build description HTML like the in-app TipTap editor (inline data:image URLs).
 */
export async function buildFeedbackDescriptionHtml(
    plainText: string,
    files: File[],
): Promise<string> {
    const parts: string[] = [];
    const text = plainText.trim();

    if (text !== '') {
        const escaped = escapeHtml(text).replace(
            /\r\n|\r|\n/g,
            '<br>',
        );
        parts.push(`<p>${escaped}</p>`);
    }

    for (const file of files.slice(0, MAX_IMAGES)) {
        const src = await dataUrlForWidgetImage(file);
        const alt = escapeHtml(file.name);
        parts.push(`<p><img src="${src}" alt="${alt}" /></p>`);
    }

    const html = parts.join('');

    if (html.length > MAX_DESCRIPTION_CHARS) {
        throw new Error(
            'Description with images is too long. Try fewer or smaller images.',
        );
    }

    return html;
}
